// ==========================================
// src/pages/FretePublico.jsx
// PÁGINA PÚBLICA DE FRETE - VERSÃO LENDÁRIA
// ✅ Meta tags dinâmicas SEM dependência de Helmet
// ✅ Open Graph com Borinho logo
// ✅ Botão "Quero essa carga" com passagem de dados
// ✅ Fallback múltiplo de busca (slug, ID curto, UUID)
// ✅ Schema.org para Google
// ✅ ÍCONES DE REDES SOCIAIS — WhatsApp, Facebook, Telegram, Twitter, LinkedIn
// ✅ Compartilhamento principal: WHATSAPP com mensagem da empresa
// ✅ Layout lendário com animações e design moderno
// ✅ CORREÇÃO: Busca por ID direto quando slug é UUID ou ID curto
// ✅ NOVO: Links de compartilhamento com carga-preview.html para WhatsApp mostrar só texto
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin, Truck, Weight, Calendar, DollarSign, ArrowLeft,
  Package, Building2, Phone, Mail, User, CheckCircle, Clock,
  Route, Box, Info, Loader2, AlertTriangle, Share2, Copy, Check,
  ExternalLink, MessageCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from '@/lib/supabase';

// ==========================================
// ÍCONES SVG DAS REDES SOCIAIS (inline, sem dependência externa)
// ==========================================
const WhatsAppIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TelegramIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// Mapeamentos
const tiposCargaMap = {
  geral: 'Carga Geral', granel_solido: 'Granel Sólido', granel_liquido: 'Granel Líquido',
  refrigerada: 'Refrigerada', perigosa: 'Perigosa', viva: 'Carga Viva',
  indivisivel: 'Indivisível', container: 'Container', sacos: 'Sacos',
  big_bag: 'Big Bag', paletizado: 'Paletizado', soja: 'Soja', milho: 'Milho',
  farelo: 'Farelo', acucar: 'Açúcar', algodao: 'Algodão', arroz: 'Arroz',
  feijao: 'Feijão', trigo: 'Trigo', cafe: 'Café', carne: 'Carne',
  frango: 'Frango', suino: 'Suíno'
};

const tiposVeiculoMap = {
  truck: 'Truck', carreta: 'Carreta', bitrem: 'Bitrem', rodotrem: 'Rodotrem',
  van: 'Van', toco: 'Toco', tres_quartos: '3/4', vuc: 'VUC',
  prancha: 'Prancha', furgão: 'Furgão'
};

const tiposCarroceriaMap = {
  aberta: 'Aberta', fechada: 'Fechada', bau: 'Baú', sider: 'Sider',
  graneleira: 'Graneleira', tanque: 'Tanque', cegonha: 'Cegonha',
  prancha: 'Prancha', boiadeiro: 'Boiadeiro', cacamba: 'Caçamba'
};

const statusMap = {
  disponivel: { label: 'Disponível', cor: 'bg-green-100 text-green-800 border-green-300' },
  em_negociacao: { label: 'Em Negociação', cor: 'bg-amber-100 text-amber-800 border-amber-300' },
  reservada: { label: 'Reservada', cor: 'bg-blue-100 text-blue-800 border-blue-300' },
  em_transporte: { label: 'Em Transporte', cor: 'bg-purple-100 text-purple-800 border-purple-300' },
  entregue: { label: 'Entregue', cor: 'bg-gray-100 text-gray-800 border-gray-300' },
  cancelada: { label: 'Cancelada', cor: 'bg-red-100 text-red-800 border-red-300' }
};

const formatarValor = (valor) => {
  const num = Number(valor);
  if (isNaN(num) || num === 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(num);
};

const formatarData = (dataStr) => {
  if (!dataStr) return 'Não informada';
  return new Date(dataStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ==========================================
// ✅ CONFIGURAÇÃO DA EMPRESA (WhatsApp)
// ==========================================
const WHATSAPP_EMPRESA = '5565981181997'; // 📱 Número da JL Trade / BoraFrete com DDD
const NOME_EMPRESA = 'BoraFrete';

// ✅ FUNÇÃO: Verificar se é UUID válido
const isUUID = (str) => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// ✅ FUNÇÃO: Verificar se é ID curto (8 primeiros chars de UUID)
const isIdCurto = (str) => {
  if (!str) return false;
  return str.length === 8 && /^[0-9a-f]{8}$/i.test(str);
};

// ✅ FUNÇÃO: Gerar slug (mesma lógica da Edge Function)
const gerarSlug = (cargaData) => {
  if (!cargaData) return '';
  const origem = `${cargaData.origem_cidade}-${cargaData.origem_estado}`
    .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const destino = `${cargaData.destino_cidade}-${cargaData.destino_estado}`
    .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const tipo = (cargaData.tipo_carga || 'carga').toLowerCase().replace(/_/g, '-').replace(/-+/g, '-');
  const peso = cargaData.peso_valor ? `${cargaData.peso_valor}-${cargaData.peso_unidade || 'ton'}` : '';
  const idCurto = cargaData.id?.split('-')[0] || '';
  const partes = ['frete', origem, 'para', destino];
  if (tipo && tipo !== 'carga') partes.push(tipo);
  if (peso) partes.push(peso);
  partes.push(idCurto);
  return partes.join('-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

// ==========================================
// ✅ FUNÇÃO: Gerar link de preview para WhatsApp (mostra só texto da carga)
// ==========================================
const gerarLinkPreview = (carga) => {
  const params = new URLSearchParams({
    id: carga.id,
    origem: carga.origem_cidade || '',
    origem_uf: carga.origem_estado || '',
    destino: carga.destino_cidade || '',
    destino_uf: carga.destino_estado || '',
    titulo: carga.titulo || '',
    valor: carga.valor_frete ? 'R$ ' + carga.valor_frete : 'A combinar',
    peso: (carga.peso_valor || '') + ' ' + (carga.peso_unidade || ''),
    veiculo: carga.tipo_veiculo || '',
    tipo: carga.tipo_carga || ''
  });
  
  // ✅ USA PÁGINA ESTÁTICA NO HOSTINGER — WhatsApp mostra SÓ TEXTO da carga
  return `https://borafrete.com.br/carga-preview.html?${params.toString()}`;
};

export default function FretePublico() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [carga, setCarga] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(false);

  // ============================================================
  // ✅ META TAGS DINÂMICAS VIA useEffect (SEM Helmet)
  // ============================================================
  const atualizarMetaTags = (cargaData) => {
    if (!cargaData) {
      document.title = 'Frete Disponível | BoraFrete';
      return;
    }

    const tipoLabel = tiposCargaMap[cargaData.tipo_carga] || 'Carga';
    const origem = `${cargaData.origem_cidade}/${cargaData.origem_estado}`;
    const destino = `${cargaData.destino_cidade}/${cargaData.destino_estado}`;
    const valor = cargaData.tipo_valor === 'a_combinar' ? 'A Combinar' : formatarValor(cargaData.valor_total || cargaData.valor_frete);
    const peso = `${cargaData.peso_valor} ${cargaData.peso_unidade || 'ton'}`;
    const urlAtual = window.location.href;
    const imagemBorinho = 'https://borafrete.com.br/borinhochat.png';

    const title = `Frete de ${tipoLabel} | ${origem} → ${destino} | ${peso} | ${valor} | BoraFrete`;
    const description = `Frete disponível: ${tipoLabel} saindo de ${origem} para ${destino}. Peso: ${peso}. Valor: ${valor}. Veículo: ${tiposVeiculoMap[cargaData.tipo_veiculo] || cargaData.tipo_veiculo}. Clique e se candidate agora no BoraFrete!`;

    // Atualiza title
    document.title = title;

    // Atualiza ou cria meta tags
    const metaTags = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: imagemBorinho },
      { property: 'og:url', content: urlAtual },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'BoraFrete' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: imagemBorinho },
      { name: 'keywords', content: `frete, ${cargaData.tipo_carga}, ${cargaData.origem_cidade}, ${cargaData.destino_cidade}, transporte, carga, caminhão, ${cargaData.origem_estado}, ${cargaData.destino_estado}` }
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${tag.property ? 'property' : 'name'}="${tag.property || tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        if (tag.property) element.setAttribute('property', tag.property);
        else element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Schema.org JSON-LD
    let schemaScript = document.getElementById('schema-frete');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'schema-frete';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": `Frete de ${tipoLabel} - ${cargaData.origem_cidade} para ${cargaData.destino_cidade}`,
      "description": description,
      "datePosted": cargaData.created_at,
      "validThrough": cargaData.data_coleta,
      "hiringOrganization": {
        "@type": "Organization",
        "name": cargaData.empresa_nome || "BoraFrete",
        "sameAs": "https://borafrete.com.br"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": cargaData.destino_cidade,
          "addressRegion": cargaData.destino_estado,
          "addressCountry": "BR"
        }
      },
      "baseSalary": cargaData.tipo_valor !== 'a_combinar' ? {
        "@type": "MonetaryAmount",
        "currency": "BRL",
        "value": {
          "@type": "QuantitativeValue",
          "value": cargaData.valor_total || cargaData.valor_frete
        }
      } : undefined
    });
  };

  // ============================================================
  // ✅ BUSCAR CARGA (múltiplas estratégias) — CORRIGIDO!
  // ============================================================
  useEffect(() => {
    const buscarCarga = async () => {
      if (!slug) {
        setError('Link inválido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Buscando carga. Slug/ID:', slug);

        let cargaEncontrada = null;

        // ✅ ESTRATÉGIA 1: UUID completo (quando o slug é um UUID)
        if (isUUID(slug)) {
          console.log('🔍 Estratégia 1: Buscando por UUID completo');
          const { data, error: err } = await supabase
            .from('cargas').select('*').eq('id', slug).maybeSingle();
          if (data && !err) {
            console.log('✅ Carga encontrada por UUID!');
            cargaEncontrada = data;
          }
        }

        // ✅ ESTRATÉGIA 2: ID curto (8 primeiros chars do UUID)
        if (!cargaEncontrada && isIdCurto(slug)) {
          console.log('🔍 Estratégia 2: Buscando por ID curto');
          const { data, error: err } = await supabase
            .from('cargas').select('*').ilike('id', `${slug}%`).maybeSingle();
          if (data && !err) {
            console.log('✅ Carga encontrada por ID curto!');
            cargaEncontrada = data;
          }
        }

        // ✅ ESTRATÉGIA 3: Comparar slugs gerados (todos os status)
        if (!cargaEncontrada) {
          console.log('🔍 Estratégia 3: Comparando slugs gerados');
          const { data: todasCargas, error: errTodas } = await supabase
            .from('cargas').select('*');  // Busca TODAS, não só disponíveis
          if (!errTodas && todasCargas) {
            cargaEncontrada = todasCargas.find(c => gerarSlug(c) === slug);
            if (cargaEncontrada) console.log('✅ Carga encontrada por slug gerado!');
          }
        }

        // ✅ ESTRATÉGIA 4: Buscar por slug exato na coluna slug
        if (!cargaEncontrada) {
          console.log('🔍 Estratégia 4: Buscando por slug na coluna');
          const { data, error: err } = await supabase
            .from('cargas').select('*').eq('slug', slug).maybeSingle();
          if (data && !err) {
            console.log('✅ Carga encontrada por coluna slug!');
            cargaEncontrada = data;
          }
        }

        // ✅ ESTRATÉGIA 5: Fallback - buscar por título similar
        if (!cargaEncontrada) {
          console.log('🔍 Estratégia 5: Busca por título similar');
          const slugLimpo = slug.replace(/-/g, ' ').replace(/frete|para/gi, '').trim();
          if (slugLimpo.length > 3) {
            const { data, error: err } = await supabase
              .from('cargas').select('*').ilike('titulo', `%${slugLimpo}%`).limit(5);
            if (data && data.length > 0 && !err) {
              console.log('✅ Carga encontrada por título similar!');
              cargaEncontrada = data[0];
            }
          }
        }

        if (cargaEncontrada) {
          console.log('✅ Carga final:', cargaEncontrada.id, cargaEncontrada.titulo);
          setCarga(cargaEncontrada);
          atualizarMetaTags(cargaEncontrada);
        } else {
          console.log('❌ Carga não encontrada em nenhuma estratégia');
          setError('Frete não encontrado. Ele pode ter sido removido, reservado ou expirado.');
          atualizarMetaTags(null);
        }

      } catch (err) {
        console.error('❌ Erro ao buscar carga:', err);
        setError('Erro ao carregar o frete. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    buscarCarga();

    // Cleanup: remove meta tags ao sair da página
    return () => {
      document.title = 'BoraFrete - Conectando Cargas e Motoristas';
      const schemaScript = document.getElementById('schema-frete');
      if (schemaScript) schemaScript.remove();
    };
  }, [slug]);

  // ============================================================
  // ✅ AÇÕES DE COMPARTILHAMENTO — REDES SOCIAIS
  // ============================================================

  // 📱 WHATSAPP — Compartilhamento principal com mensagem da empresa
  const compartilharWhatsApp = () => {
    if (!carga) return;
    
    const tipoLabel = tiposCargaMap[carga.tipo_carga] || 'Carga';
    const valorTotal = carga.tipo_valor === 'a_combinar' ? 'A Combinar' : formatarValor(carga.valor_total || carga.valor_frete);
    
    // ✅ USA LINK PREVIEW (carga-preview.html) — WhatsApp mostra SÓ TEXTO da carga
    const linkPreview = gerarLinkPreview(carga);
    
    // ✅ MENSAGEM DA EMPRESA — Profissional e completa
    const texto = `🚛 *FRETE DISPONÍVEL no ${NOME_EMPRESA}!*%0A%0A` +
      `📦 *${carga.titulo}*%0A` +
      `📍 *Origem:* ${carga.origem_cidade}/${carga.origem_estado}%0A` +
      `🏁 *Destino:* ${carga.destino_cidade}/${carga.destino_estado}%0A` +
      `⚖️ *Peso:* ${carga.peso_valor} ${carga.peso_unidade || 'ton'}%0A` +
      `🚛 *Veículo:* ${tiposVeiculoMap[carga.tipo_veiculo] || carga.tipo_veiculo}%0A` +
      `💰 *Valor:* ${valorTotal}%0A%0A` +
      `👉 *Clique e se candidate agora:*%0A${linkPreview}%0A%0A` +
      `📲 *Dúvidas? Fale com a ${NOME_EMPRESA}:*%0A` +
      `https://wa.me/${WHATSAPP_EMPRESA}`;
    
    window.open(`https://wa.me/?text=${texto}`, '_blank');
  };

  // 📱 WHATSAPP DIRETO — Falar com a empresa
  const falarComEmpresaWhatsApp = () => {
    const texto = `Olá! Vi um frete no ${NOME_EMPRESA} e gostaria de mais informações.`;
    window.open(`https://wa.me/${WHATSAPP_EMPRESA}?text=${encodeURIComponent(texto)}`, '_blank');
  };

  // 📘 FACEBOOK
  const compartilharFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  // ✈️ TELEGRAM
  const compartilharTelegram = () => {
    const url = encodeURIComponent(window.location.href);
    const texto = encodeURIComponent(`Frete disponível no ${NOME_EMPRESA}!`);
    window.open(`https://t.me/share/url?url=${url}&text=${texto}`, '_blank');
  };

  // 🐦 TWITTER/X
  const compartilharTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const texto = encodeURIComponent(`🚛 Frete disponível no ${NOME_EMPRESA}! ${carga?.titulo || ''}`);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${texto}`, '_blank', 'width=600,height=400');
  };

  // 💼 LINKEDIN
  const compartilharLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  // 📋 COPIAR LINK
  const copiarLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopiado(true);
      toast.success('Link copiado! Cole onde quiser.');
      setTimeout(() => setCopiado(false), 3000);
    });
  };

  const queroEssaCarga = () => {
    if (!carga) return;
    
    // Salva no sessionStorage para o login usar depois
    sessionStorage.setItem('borafrete_carga_interesse', JSON.stringify({
      cargaId: carga.id,
      titulo: carga.titulo,
      origem: `${carga.origem_cidade}/${carga.origem_estado}`,
      destino: `${carga.destino_cidade}/${carga.destino_estado}`,
      valor: carga.valor_total || carga.valor_frete,
      tipo: carga.tipo_carga,
      slug: slug
    }));
    
    // Redireciona para login com redirect
    navigate(`/login?redirect=/carga/${carga.id}&interesse=true`);
  };

  const irParaBuscarCargas = () => navigate('/buscar-cargas');
  const irParaLogin = () => navigate('/login');

  // ============================================================
  // RENDER: LOADING
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-6">
            <img src="/borinhochat.png" alt="Borinho" className="w-24 h-24 mx-auto animate-bounce" />
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B35]" />
            </div>
          </div>
          <p className="text-slate-600 font-medium">Carregando detalhes do frete...</p>
          <p className="text-sm text-slate-400 mt-2">Borinho está buscando sua carga!</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: ERRO
  // ============================================================
  if (error || !carga) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <img src="/borinhochat.png" alt="Borinho" className="w-20 h-20 mx-auto mb-4 opacity-50" />
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Frete Não Encontrado</h1>
          <p className="text-slate-600 mb-6">{error || 'Este frete pode ter sido removido ou já foi reservado.'}</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button variant="outline" onClick={irParaBuscarCargas}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ver Outros Fretes
            </Button>
            <Button onClick={irParaLogin} className="bg-[#FF6B35] hover:bg-[#E85A2A]">
              <ExternalLink className="w-4 h-4 mr-2" />
              Fazer Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: CARGA ENCONTRADA
  // ============================================================
  const statusInfo = statusMap[carga.status] || statusMap.disponivel;
  const tipoLabel = tiposCargaMap[carga.tipo_carga] || carga.tipo_carga;
  const veiculoLabel = tiposVeiculoMap[carga.tipo_veiculo] || carga.tipo_veiculo;
  const carroceriaLabel = tiposCarroceriaMap[carga.tipo_carroceria] || carga.tipo_carroceria;
  const valorDisplay = carga.tipo_valor === 'a_combinar' ? 'A Combinar' : formatarValor(carga.valor_total || carga.valor_frete);

  // ✅ LINK PREVIEW para compartilhamento (WhatsApp mostra só texto)
  const linkPreviewCompartilhamento = gerarLinkPreview(carga);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      {/* HEADER COM BORINHO */}
      <div className="bg-[#1E3A5F] text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/borinhochat.png" alt="Borinho" className="w-10 h-10 rounded-full bg-white/10" />
            <div>
              <h2 className="font-bold text-lg">BoraFrete</h2>
              <p className="text-xs text-white/70">Conectando cargas e motoristas</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={irParaBuscarCargas} className="text-white hover:bg-white/10">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* STATUS E TÍTULO */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Badge className={`${statusInfo.cor} text-sm px-3 py-1`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              {statusInfo.label}
            </Badge>
            <span className="text-sm text-slate-500">
              Publicado em {formatarData(carga.created_at)}
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] leading-tight">
            Frete de {tipoLabel}
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mt-2 flex items-center gap-2 flex-wrap">
            <MapPin className="w-5 h-5 text-green-600" />
            {carga.origem_cidade}/{carga.origem_estado}
            <ArrowLeft className="w-5 h-5 text-[#FF6B35] rotate-180" />
            <MapPin className="w-5 h-5 text-red-600" />
            {carga.destino_cidade}/{carga.destino_estado}
          </p>
        </div>

        {/* ==========================================
            BOTÕES DE AÇÃO PRINCIPAIS — LENDÁRIO
        ========================================== */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          {/* 🚛 QUERO ESSA CARGA — Principal */}
          <Button 
            onClick={queroEssaCarga}
            className="flex-1 min-w-[200px] bg-[#FF6B35] hover:bg-[#E85A2A] text-white font-bold py-6 text-lg shadow-lg shadow-orange-200 transition-all hover:scale-[1.02]"
          >
            <Truck className="w-6 h-6 mr-2" />
            Quero Essa Carga
          </Button>
          
          {/* 📱 FALAR NO WHATSAPP — Direto com a empresa */}
          <Button 
            onClick={falarComEmpresaWhatsApp}
            variant="outline"
            className="px-6 py-6 border-2 border-green-500 text-green-700 hover:bg-green-50 hover:scale-[1.02] transition-all"
          >
            <WhatsAppIcon className="w-6 h-6 mr-2" />
            Falar no WhatsApp
          </Button>
        </div>

        {/* ==========================================
            🔥 COMPARTILHAR NAS REDES SOCIAIS — LENDÁRIO
            ✅ FIX: Usa linkPreviewCompartilhamento (carga-preview.html)
            WhatsApp mostra SÓ TEXTO da carga, sem imagem grande do Borinho
        ========================================== */}
        <Card className="mb-8 border-2 border-[#FF6B35]/20 bg-gradient-to-r from-orange-50/50 to-white shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
              <Share2 className="w-5 h-5 text-[#FF6B35]" />
              Compartilhar Este Frete
            </CardTitle>
            <p className="text-sm text-slate-500">
              Compartilhe com motoristas e amigos nas redes sociais
            </p>
          </CardHeader>
          <CardContent>
            {/* ÍCONES DAS REDES SOCIAIS */}
            <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
              
              {/* 📱 WHATSAPP — Principal (destaque) */}
              <button
                onClick={compartilharWhatsApp}
                className="group relative flex items-center gap-2 px-5 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold shadow-lg shadow-green-200 transition-all hover:scale-110 hover:-translate-y-1"
                title="Compartilhar no WhatsApp"
              >
                <WhatsAppIcon className="w-6 h-6" />
                <span className="hidden sm:inline">WhatsApp</span>
                <span className="absolute -top-2 -right-2 bg-[#FF6B35] text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                  Principal
                </span>
              </button>

              {/* 📘 FACEBOOK */}
              <button
                onClick={compartilharFacebook}
                className="group flex items-center justify-center w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-200 transition-all hover:scale-110 hover:-translate-y-1"
                title="Compartilhar no Facebook"
              >
                <FacebookIcon className="w-6 h-6" />
              </button>

              {/* ✈️ TELEGRAM */}
              <button
                onClick={compartilharTelegram}
                className="group flex items-center justify-center w-12 h-12 bg-sky-500 hover:bg-sky-600 text-white rounded-xl shadow-lg shadow-sky-200 transition-all hover:scale-110 hover:-translate-y-1"
                title="Compartilhar no Telegram"
              >
                <TelegramIcon className="w-6 h-6" />
              </button>

              {/* 🐦 TWITTER/X */}
              <button
                onClick={compartilharTwitter}
                className="group flex items-center justify-center w-12 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg shadow-slate-200 transition-all hover:scale-110 hover:-translate-y-1"
                title="Compartilhar no Twitter/X"
              >
                <TwitterIcon className="w-5 h-5" />
              </button>

              {/* 💼 LINKEDIN */}
              <button
                onClick={compartilharLinkedIn}
                className="group flex items-center justify-center w-12 h-12 bg-blue-700 hover:bg-blue-800 text-white rounded-xl shadow-lg shadow-blue-200 transition-all hover:scale-110 hover:-translate-y-1"
                title="Compartilhar no LinkedIn"
              >
                <LinkedInIcon className="w-5 h-5" />
              </button>

              {/* 📋 COPIAR LINK */}
              <button
                onClick={copiarLink}
                className={`group flex items-center gap-2 px-4 py-3 rounded-xl font-medium shadow-lg transition-all hover:scale-110 hover:-translate-y-1 ${
                  copiado 
                    ? 'bg-green-100 text-green-700 border-2 border-green-400' 
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-2 border-slate-300'
                }`}
                title="Copiar link"
              >
                {copiado ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                <span className="hidden sm:inline text-sm">{copiado ? 'Copiado!' : 'Copiar Link'}</span>
              </button>
            </div>

            {/* ✅ NOVO: Link de preview visível */}
            <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Link de Preview (WhatsApp)</p>
              <p className="text-xs font-mono text-slate-700 break-all">{linkPreviewCompartilhamento}</p>
            </div>

            {/* Mensagem informativa */}
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <MessageCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">
                <strong>💡 Dica:</strong> O compartilhamento pelo <strong>WhatsApp</strong> usa um link especial que mostra <strong>só o texto da carga</strong> (origem, destino, valor, peso), sem imagem grande do Borinho tampando as informações!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* GRID DE DETALHES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* ROTA */}
          <Card className="border-l-4 border-l-[#FF6B35] shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <Route className="w-5 h-5 text-[#FF6B35]" />
                Rota
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Origem</p>
                  <p className="text-slate-600">{carga.origem_cidade}, {carga.origem_estado}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Destino</p>
                  <p className="text-slate-600">{carga.destino_cidade}, {carga.destino_estado}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CARGA */}
          <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <Package className="w-5 h-5 text-blue-500" />
                Detalhes da Carga
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500">Tipo:</span>
                <Badge variant="outline" className="font-semibold">{tipoLabel}</Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500 flex items-center gap-2">
                  <Weight className="w-4 h-4" />
                  Peso:
                </span>
                <span className="font-bold text-slate-900">{carga.peso_valor} {carga.peso_unidade || 'ton'}</span>
              </div>
              {carga.metros_cubicos && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-500 flex items-center gap-2">
                    <Box className="w-4 h-4" />
                    Volume:
                  </span>
                  <span className="font-semibold">{carga.metros_cubicos} m³</span>
                </div>
              )}
              {carga.tipo_carroceria && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Carroceria:</span>
                  <span className="font-semibold">{carroceriaLabel}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* VEÍCULO */}
          <Card className="border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <Truck className="w-5 h-5 text-purple-500" />
                Veículo Necessário
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500">Tipo:</span>
                <Badge className="bg-purple-100 text-purple-800 border-purple-300 font-bold">
                  {veiculoLabel}
                </Badge>
              </div>
              {carga.tipo_carroceria && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Carroceria:</span>
                  <span className="font-semibold">{carroceriaLabel}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* VALOR */}
          <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <DollarSign className="w-5 h-5 text-green-500" />
                Valor do Frete
              </CardTitle>
            </CardHeader>
            <CardContent>
              {carga.tipo_valor === 'a_combinar' ? (
                <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-3xl font-bold text-amber-700">A Combinar</p>
                  <p className="text-sm text-amber-600 mt-2">Valor a negociar diretamente</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-500 capitalize">{carga.tipo_valor?.replace('_', ' ')}:</span>
                    <span className="font-semibold">{formatarValor(carga.valor_frete)}</span>
                  </div>
                  <div className="text-center py-4 bg-green-50 rounded-lg mt-3 border border-green-200">
                    <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Total do Frete</p>
                    <p className="text-3xl font-bold text-green-700">{formatarValor(carga.valor_total || carga.valor_frete)}</p>
                    {carga.tipo_valor === 'tonelada' && (
                      <p className="text-sm text-green-600 mt-1">
                        {formatarValor(carga.valor_frete)} × {carga.peso_valor} {carga.peso_unidade}
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* DATAS */}
          <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <Calendar className="w-5 h-5 text-amber-500" />
                Datas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Coleta:
                </span>
                <span className="font-bold text-slate-900">{formatarData(carga.data_coleta)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-500 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Entrega:
                </span>
                <span className="font-bold text-slate-900">{formatarData(carga.data_entrega)}</span>
              </div>
            </CardContent>
          </Card>

          {/* EMPRESA */}
          <Card className="border-l-4 border-l-slate-400 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-[#1E3A5F]">
                <Building2 className="w-5 h-5 text-slate-500" />
                Contratante
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-500">Empresa:</span>
                <span className="font-bold text-slate-900">{carga.empresa_nome || 'Não informado'}</span>
              </div>
              {carga.empresa_cnpj && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-500">CNPJ:</span>
                  <span className="font-mono text-sm text-slate-700">{carga.empresa_cnpj}</span>
                </div>
              )}
              {carga.empresa_cidade && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-500">Local:</span>
                  <span className="text-slate-700">{carga.empresa_cidade}, {carga.empresa_estado}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* DESCRIÇÃO */}
        {carga.descricao && (
          <Card className="mt-6 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-[#1E3A5F]">
                <Info className="w-5 h-5 text-blue-500" />
                Informações Adicionais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{carga.descricao}</p>
            </CardContent>
          </Card>
        )}

        {/* ==========================================
            CTA FINAL — LENDÁRIO
        ========================================== */}
        <div className="mt-10 text-center bg-gradient-to-r from-[#FF6B35]/10 to-orange-100/50 rounded-2xl p-8 border border-[#FF6B35]/20">
          <img src="/borinhochat.png" alt="Borinho" className="w-16 h-16 mx-auto mb-4 animate-bounce" />
          <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">Interessado neste frete?</h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Faça login ou cadastre-se gratuitamente no BoraFrete. Leva menos de 2 minutos!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={queroEssaCarga}
              className="bg-[#FF6B35] hover:bg-[#E85A2A] text-white font-bold px-10 py-6 text-lg shadow-lg shadow-orange-200 transition-all hover:scale-[1.05]"
            >
              <Truck className="w-6 h-6 mr-2" />
              Quero Essa Carga
            </Button>
            
            <Button 
              onClick={falarComEmpresaWhatsApp}
              variant="outline"
              className="px-8 py-6 border-2 border-green-500 text-green-700 hover:bg-green-50 hover:scale-[1.05] transition-all"
            >
              <WhatsAppIcon className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </Button>
          </div>
          
          <p className="text-sm text-slate-500 mt-4">
            Você será redirecionado para o login e depois poderá se candidatar ao frete.
          </p>
        </div>

        {/* FOOTER */}
        <div className="mt-12 text-center border-t border-slate-200 pt-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/borinhochat.png" alt="Borinho" className="w-8 h-8" />
            <span className="font-bold text-[#1E3A5F]">BoraFrete</span>
          </div>
          <p className="text-sm text-slate-500">
            A maior plataforma de fretes do Brasil. Conectando empresas e motoristas desde 2024.
          </p>
        </div>
      </div>
    </div>
  );
}